import type { GencowProcedureDef } from "@gencow/core";

type ProcedureRouteMaps = {
  queryProcedureMap: Map<string, GencowProcedureDef<"query", any, any, any>>;
  mutationProcedureMap: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
};

type MergeProcedureRouteMapsOptions = {
  logger?: Pick<Console, "warn" | "debug">;
};

function asProcedureDef(value: unknown): GencowProcedureDef | undefined {
  if (!value || typeof value !== "object") return undefined;
  const d = value as GencowProcedureDef;
  if (d.kind !== "query" && d.kind !== "mutation" && d.kind !== "internal") return undefined;
  if (typeof d.name !== "string" || typeof d.handler !== "function") return undefined;
  return { ...d, allowAnonymous: d.allowAnonymous === true };
}

/**
 * Reads `defineApi({ procedures, crud })` from the loaded app module (default export)
 * and builds route maps. Does not use global registries — `crud` values expose procedure defs.
 */
export function collectProcedureRouteMaps(appModule: unknown): {
  queryProcedureMap: Map<string, GencowProcedureDef<"query", any, any, any>>;
  mutationProcedureMap: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
  internalProcedureMap: Map<string, GencowProcedureDef<"internal", any, any, any>>;
} {
  const queryProcedureMap = new Map<string, GencowProcedureDef<"query", any, any, any>>();
  const mutationProcedureMap = new Map<string, GencowProcedureDef<"mutation", any, any, any>>();
  const internalProcedureMap = new Map<string, GencowProcedureDef<"internal", any, any, any>>();

  if (!appModule || typeof appModule !== "object") {
    return { queryProcedureMap, mutationProcedureMap, internalProcedureMap };
  }

  const mod = appModule as { default?: unknown };
  const apiCandidate =
    mod.default !== undefined && mod.default !== null && typeof mod.default === "object"
      ? mod.default
      : appModule;

  const procedures = (apiCandidate as { procedures?: unknown }).procedures;
  if (procedures && typeof procedures === "object") {
    for (const def of Object.values(procedures)) {
      const proc = asProcedureDef(def);
      if (!proc) continue;
      if (proc.kind === "query") {
        queryProcedureMap.set(proc.name, proc as GencowProcedureDef<"query", any, any, any>);
      } else if (proc.kind === "mutation") {
        mutationProcedureMap.set(proc.name, proc as GencowProcedureDef<"mutation", any, any, any>);
      } else {
        internalProcedureMap.set(proc.name, proc as GencowProcedureDef<"internal", any, any, any>);
      }
    }
  }

  const crudBag = (apiCandidate as { crud?: Record<string, unknown> }).crud;
  if (crudBag && typeof crudBag === "object") {
    for (const entry of Object.values(crudBag)) {
      if (!entry || typeof entry !== "object") continue;
      const maybe = entry as Record<string, unknown>;
      for (const key of ["list", "get", "create", "update", "remove"] as const) {
        const proc = asProcedureDef(maybe[key]);
        if (!proc) continue;
        if (proc.kind === "query") {
          queryProcedureMap.set(proc.name, proc as GencowProcedureDef<"query", any, any, any>);
        } else if (proc.kind === "mutation") {
          mutationProcedureMap.set(proc.name, proc as GencowProcedureDef<"mutation", any, any, any>);
        }
      }
    }
  }

  return { queryProcedureMap, mutationProcedureMap, internalProcedureMap };
}

export function mergeProcedureRouteMaps(
  appMaps: ProcedureRouteMaps,
  builtinMaps: ProcedureRouteMaps,
  options: MergeProcedureRouteMapsOptions = {},
): ProcedureRouteMaps {
  const queryProcedureMap = new Map(appMaps.queryProcedureMap);
  const mutationProcedureMap = new Map(appMaps.mutationProcedureMap);
  const appProcedureNames = new Set([
    ...appMaps.queryProcedureMap.keys(),
    ...appMaps.mutationProcedureMap.keys(),
  ]);

  mergeBuiltinMap({
    target: queryProcedureMap,
    builtin: builtinMaps.queryProcedureMap,
    appProcedureNames,
    logger: options.logger,
  });
  mergeBuiltinMap({
    target: mutationProcedureMap,
    builtin: builtinMaps.mutationProcedureMap,
    appProcedureNames,
    logger: options.logger,
  });

  return { queryProcedureMap, mutationProcedureMap };
}

function mergeBuiltinMap<TKind extends "query" | "mutation">(params: {
  target: Map<string, GencowProcedureDef<TKind, any, any, any>>;
  builtin: Map<string, GencowProcedureDef<TKind, any, any, any>>;
  appProcedureNames: ReadonlySet<string>;
  logger?: Pick<Console, "warn" | "debug">;
}): void {
  for (const [name, def] of params.builtin) {
    if (params.appProcedureNames.has(name)) {
      const message = `[gencow] Builtin procedure "${name}" shadowed by app defineApi procedure.`;
      if (typeof params.logger?.debug === "function") params.logger.debug(message);
      else params.logger?.warn?.(message);
      continue;
    }
    params.target.set(name, def);
  }
}
